
import RcDomainObject from "@/domain/common/RcDomainObject.js";
import StringUtils  from '@/utils/StringUtils.js';

export default class TimesheetEntry extends RcDomainObject {
  
  static MODEL_NAME = "TimesheetEntry";
  
  constructor(domain, data) {
    super(domain, data, TimesheetEntry.MODEL_NAME);
  }
  
  static Fields = {
    HOUR_END: "endHour",
    QUARTER_END: "endQuarter",
    TIME_ID_END: "endTimeId",
    LOCATION: "location",
    REMARKS: "remarks",
    HOUR_START: "startHour",
    QUARTER_START: "startQuarter",
    TIME_ID_START: "startTimeId",
    ENTRY_TYPE: "type",
  }
  
  static Values = {
    EntryType: {
      ON_DUTY_NOT_DRIVING: "onDutyNotDriving",
      ON_DUTY_DRIVING: "onDutyDriving",
      OFF_DUTY_AWAY: "offDuty",
      OFF_DUTY_SLEEPER: "offDutySleeper",
      
      isOffDutyAway: function(entryType) {
        return this.OFF_DUTY_AWAY.toUpperCase() === entryType.toUpperCase();
      },
      isOnDutyDriving: function(entryType) {
        return this.ON_DUTY_DRIVING.toUpperCase() === entryType.toUpperCase();
      },
      isOnDutyNotDriving: function(entryType) {
        return this.ON_DUTY_NOT_DRIVING.toUpperCase() === entryType.toUpperCase();
      }
    },
    Quarters: {
      ONE: 0,
      TWO: 1,
      THREE: 2,
      FOUR: 3,
      FIVE: 4,
      LAST_QUARTER: 95,
      MAX_QUARTERS: 96,
    },
  }
  
  fields() {
    return TimesheetEntry.FIELDS;
  }
  
  static newTimeId(hour, quarter) {
    var id = hour * 4 + quarter;
    return id;
  }
  
  totalQuarters() {
    var total = this.timeIdEnd() - this.timeIdStart();
    total = total + 1;
    return total;
  }
  
  quarterStart() {
    return this.get(TimesheetEntry.Fields.QUARTER_START);
  }
  
  hourStart() {
    return this.get(TimesheetEntry.Fields.HOUR_START);
  }
  
  quarterEnd() {
    return this.get(TimesheetEntry.Fields.QUARTER_END);
  }
  
  hourEnd() {
    return this.get(TimesheetEntry.Fields.HOUR_END);
  }
  
  timeIdStart() {
    return this.get(TimesheetEntry.Fields.TIME_ID_START);
  }
  
  isTimeIdStartSet() {
    var result = this.timeIdStart();
    return result >= 0;
  }
  
  timeIdEnd() {
    return this.get(TimesheetEntry.Fields.TIME_ID_END);
  }
  
  entryType() {
    return this.getString(TimesheetEntry.Fields.ENTRY_TYPE);
  }
  
  entryTypeDisplay() {
    var entryType = this.entryType();
    var display = "";
    switch (entryType) {
      case TimesheetEntry.Values.EntryType.OFF_DUTY_AWAY:
        display = "Off Duty";
        break;
      case TimesheetEntry.Values.EntryType.OFF_DUTY_SLEEPER:
        display = "Sleeper";
        break;
      case TimesheetEntry.Values.EntryType.ON_DUTY_DRIVING:
        display = "Driving";
        break;
      case TimesheetEntry.Values.EntryType.ON_DUTY_NOT_DRIVING:
        display = "On Duty Not Driving";
        break;
      default:
        break;
    }
    return display;
  }

  isEntryType(entryType) {
    return StringUtils.equals(this.entryType(), entryType);
  }
  
  isOffDuty() {
    return StringUtils.equals(TimesheetEntry.Values.EntryType.OFF_DUTY_AWAY, this.entryType());
  }
  
  isOffDutySleeper() {
    return StringUtils.equals(TimesheetEntry.Values.EntryType.OFF_DUTY_SLEEPER, this.entryType());
  }
  
  isOnDutyDriving() {
    return StringUtils.equals(TimesheetEntry.Values.EntryType.ON_DUTY_DRIVING, this.entryType());
  }
  
  isOnDuty() {
    return StringUtils.equals(TimesheetEntry.Values.EntryType.ON_DUTY_NOT_DRIVING, this.entryType());
  }
  
  remarks() {
    return this.getString(TimesheetEntry.Fields.REMARKS);
  }
  
  location() {
    return this.getString(TimesheetEntry.Fields.LOCATION);
  }

  durationDisplay() {
    return StringUtils.format("{0} - {1}", [this.hourStartDisplay(), this.hourEndDisplay()]);
  }

  hourStartDisplay() {
    var quarter = this.quarterStart();
    var quarterDisplay = TimesheetEntry.quarterStartDisplay(quarter);
    var clock = TimesheetEntry.toTwelveHourClock(this.hourStart());
    return StringUtils.format("{0}:{1} {2}", [clock.hour, quarterDisplay, clock.meridiem]);
  }

  /**
   * The hour of a 24 hour day on a 12 hour clock, with the half of the day it falls in.
   * Noon is the case worth stating: it is 12 PM, not 12 AM, and testing for the
   * afternoon with hour > 12 quietly excluded it.
   */
  static toTwelveHourClock(hour) {
    var meridiem = (hour >= 12 && hour < 24) ? "PM" : "AM";
    var displayHour = hour % 12;
    if (displayHour === 0) {
      displayHour = 12;
    }
    return { hour: displayHour, meridiem: meridiem };
  }

  static quarterStartDisplay(quarter) {
    var quarterStr = "00";
    switch (quarter) {
      case TimesheetEntry.Values.Quarters.ONE:
        quarterStr = "00";
        break;
      case TimesheetEntry.Values.Quarters.TWO:
        quarterStr = "15";
        break;
      case TimesheetEntry.Values.Quarters.THREE:
        quarterStr = "30";
        break;
      default:
        quarterStr = "45";
        break;
    }
    return quarterStr;
  }

  hourEndDisplay() {
    var quarter = this.quarterEnd();
    var hour = this.hourEnd();
    if (quarter == TimesheetEntry.Values.Quarters.FOUR) {
      // The last quarter of an hour runs up to the top of the next one.
      hour = hour + 1;
    }
    var clock = TimesheetEntry.toTwelveHourClock(hour);
    var quarterDisplay = TimesheetEntry.quarterEndDisplay(quarter);

    return StringUtils.format("{0}:{1} {2}", [clock.hour, quarterDisplay, clock.meridiem]);
  }

  static quarterEndDisplay(quarter) {
    var quarterStr = "00";
    switch (quarter) {
      case TimesheetEntry.Values.Quarters.ONE:
        quarterStr = "15";
        break;
      case TimesheetEntry.Values.Quarters.TWO:
        quarterStr = "30";
        break;
      case TimesheetEntry.Values.Quarters.THREE:
        quarterStr = "45";
        break;
      default:
        quarterStr = "00";
        break;
    }
    return quarterStr;
  }
}